<template>
  <div>
    <template v-for="(part, index) in descriptionParts" :key="index">
      <span v-if="part.type === 'html'" v-html="part.content" />
      <span v-else :class="$style.exclusiveMaskingContainer">
        <HgLink @click="onClickMaskedContent">{{ part.content }}</HgLink>
        <TenantPlusBadge small :class="$style.exclusiveMaskingSpacing" />
      </span>
    </template>
  </div>
</template>

<script lang="ts" setup>
import TenantPlusBadge from '@/components/TenantPlus/Badge/TenantPlusBadge.vue';
import { InteractionEvent } from '@/helpers/GTM/GTMParameters';
import { trackEventHarmonized } from '@/helpers/GTM/GTMTrack';
import { sanitizeHtml } from '@/helpers/Sanitize/Sanitize';
import { HgLink } from '@smg-real-estate/domus';
import { computed } from 'vue';

defineOptions({
  name: 'MaskedDescription',
});

interface Props {
  description: string;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  maskedContentClicked: [];
}>();

const onClickMaskedContent = () => {
  trackEventHarmonized(InteractionEvent.PDP_INTERACTION, {
    h_event_category_006: InteractionEvent.PDP_INTERACTION,
    h_event_action_007: 'open_desc_hide_contact',
  });
  emit('maskedContentClicked');
};

type DescriptionPart = { type: 'html'; content: string } | { type: 'masked'; content: string };

const PLACEHOLDER_REGEXP = /<span class="tenantPlusExclusive">(.*?)<\/span>/g;

/**
 * Split the description into inert HTML segments and masked contact placeholders.
 *
 * Each HTML segment is sanitised and rendered via `v-html`; each placeholder is
 * rendered as real `<HgLink>`/`<TenantPlusBadge>` components. This deliberately
 * avoids the previous approach of concatenating the listing-provided text into a
 * template string and compiling it with Vue's runtime compiler — that path would
 * execute any injected `{{ }}` interpolation or `v-`/`:`/`@` directive present in
 * the description (template injection), which HTML sanitisation alone cannot stop.
 */
const descriptionParts = computed<DescriptionPart[]>(() => {
  const parts: DescriptionPart[] = [];
  const regexp = new RegExp(PLACEHOLDER_REGEXP);
  const { description } = props;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regexp.exec(description)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        type: 'html',
        content: sanitizeHtml(description.slice(lastIndex, match.index)),
      });
    }
    parts.push({ type: 'masked', content: match[1] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < description.length) {
    parts.push({ type: 'html', content: sanitizeHtml(description.slice(lastIndex)) });
  }

  return parts;
});
</script>

<style lang="scss" module>
@import '@/assets/variables';

.exclusiveMaskingSpacing {
  margin-left: $space-50;
}

.exclusiveMaskingContainer {
  display: inline-block;
  font-weight: normal;
}
</style>
